import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import type { WeeklyOffSettingConfig } from "@vTypes/weeklyOff";

type WeeklyOffSettingLocal = Omit<
    WeeklyOffSettingConfig,
    "cycle_start_date"
> & {
    cycle_start_date: string; // 문자열로 관리
};

export function useWeeklyOffSettings(
    weeklyOffSettingsData: WeeklyOffSettingConfig | undefined,
    refetchSettings?: () => Promise<any>
) {
    // API에서 받아온 주휴 설정을 컴포넌트 상태로 관리
    const [weeklyOffSettings, setWeeklyOffSettings] =
        useState<WeeklyOffSettingLocal | null>(null);

    // 초기 설정값 저장을 위한 ref
    const initialSettingsRef = useRef<WeeklyOffSettingLocal | null>(null);
    const hasManualChangesRef = useRef(false);

    // weekOffset 상태
    const [weekOffset, setWeekOffset] = useState<number>(
        weeklyOffSettingsData?.shift_variation ?? 0
    );

    // API 응답 데이터를 상태로 설정
    useEffect(() => {
        if (weeklyOffSettingsData) {
            const formattedSettings = {
                ...weeklyOffSettingsData,
                cycle_start_date: weeklyOffSettingsData.cycle_start_date
                    ? new Date(weeklyOffSettingsData.cycle_start_date)
                        .toISOString()
                        .split("T")[0]
                    : "",
            };
            setWeeklyOffSettings(formattedSettings as WeeklyOffSettingLocal);

            // 최초 데이터 로드 시에만 초기값 설정
            // if (!initialSettingsRef.current) {
            //     initialSettingsRef.current = formattedSettings as WeeklyOffSettingLocal;
            // }
            if (!hasManualChangesRef.current) { // 기존: if (!initialSettingsRef.current)
                initialSettingsRef.current = formattedSettings as WeeklyOffSettingLocal;
            }
        }
    }, [weeklyOffSettingsData]);

    // 설정값으로 weekOffset 상태 동기화
    useEffect(() => {
        if (weeklyOffSettings) {
            setWeekOffset(weeklyOffSettings.shift_variation ?? 0);
        }
    }, [weeklyOffSettings]);

    // 설정 변경 핸들러
    const handleSettingsChange = useCallback((
        e:
            | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
            | {


                target: {
                    name: string;
                    value: string;
                    type?: string;
                    checked?: boolean;
                };
            }
    ) => {
        hasManualChangesRef.current = true;
        const { name, value, type } = e.target;
        const isCheckbox = type === "checkbox";
        const isNumberInput =
            (type === "number" || name === "shift_variation") &&
            name !== "cycle_start_date";

        setWeeklyOffSettings((prev) => {
            // ✅ prev가 null이면 빈 객체부터 시작 (타입 수정)
            const base = prev ?? {} as Partial<WeeklyOffSettingLocal>;

            const newValue = isCheckbox
                ? (e.target as HTMLInputElement).checked
                : isNumberInput
                    ? Number(value)
                    : value;

            const next = { ...base, [name]: newValue } as WeeklyOffSettingLocal;
            return next;
        });
    }, []);

    // 설정 변경사항 감지
    const hasSettingsChanges = useMemo(() => {
        if (!weeklyOffSettings || !initialSettingsRef.current) return false;
        return JSON.stringify(weeklyOffSettings) !== JSON.stringify(initialSettingsRef.current);
    }, [weeklyOffSettings]);

    // 날짜를 YYYY-MM-DD로 자동 포맷팅하는 함수
    const formatDate = useCallback((value: string) => {
        const numbers = value.replace(/\D/g, "");

        if (numbers.length <= 4) return numbers; // YYYY
        if (numbers.length <= 6)
            return `${numbers.slice(0, 4)}-${numbers.slice(4)}`; // YYYY-MM
        return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`; // YYYY-MM-DD
    }, []);

    // Date 값을 문자열로 변환
    const getDateValue = useCallback((value: string | Date | undefined | null): string => {
        if (!value) return "";
        if (value instanceof Date) {
            return value.toISOString().split("T")[0];
        }
        return value;
    }, []);

    // 날짜 문자열 유효성 검사
    const isValidDateString = useCallback((str: string): boolean => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return false;

        const d = new Date(str);
        if (isNaN(d.getTime())) return false;

        // 달력 상 날짜 비교 (ex: 2025-02-31 → JS는 3/3으로 변환됨 → 잘못된 날짜!)
        const [yyyy, mm, dd] = str.split("-").map(Number);
        return (
            d.getFullYear() === yyyy && d.getMonth() + 1 === mm && d.getDate() === dd
        );
    }, []);

    // 초기 설정 상태 업데이트 (저장 후)
    const updateInitialSettings = useCallback(async () => {
        // 1. 먼저 최신 데이터 refetch
        if (refetchSettings) {
            await refetchSettings();
        }

        // 2. 초기 상태 업데이트 및 플래그 리셋
        if (weeklyOffSettings) {
            initialSettingsRef.current = weeklyOffSettings;
            hasManualChangesRef.current = false;
        }
    }, [weeklyOffSettings, refetchSettings]);

    // 상태를 초기값으로 되돌리는 함수
    const resetSettings = useCallback(() => {
        if (initialSettingsRef.current) {
            setWeeklyOffSettings(initialSettingsRef.current);
        }
    }, []);

    // 저장용 payload 생성
    const getSettingsPayload = useCallback(() => {
        if (!weeklyOffSettings) return null;

        const convertToValidDate = (str: string) => {
            const d = new Date(str);
            return isNaN(d.getTime()) ? null : d;
        };

        const dateObj = convertToValidDate(weeklyOffSettings.cycle_start_date);

        if (!dateObj) {
            return null;
        }

        // 수동 순환일 때
        if (weeklyOffSettings.use_variable_cycle === false) {
            return {
                // 주휴설정 on되어 있다면 activate 는 백에서 자동으로 true되게 처리되었다고 함.
                // activate: weeklyOffSettings.activate,
                use_variable_cycle: weeklyOffSettings.use_variable_cycle,
                cycle_start_date: dateObj,
            };
        }

        // 자동 순환일 때
        // cycle_type==="month"일 경우 interval 제외
        if (weeklyOffSettings.cycle_type === "month") {
            const { cycle_interval, ...rest } = weeklyOffSettings;
            return {
                ...rest,
                cycle_start_date: dateObj,
            };
        }

        return {
            ...weeklyOffSettings,
            cycle_start_date: dateObj,
        };
    }, [weeklyOffSettings]);

    return {
        // 데이터
        weeklyOffSettings,
        weekOffset,

        // 설정 함수
        setWeeklyOffSettings,
        setWeekOffset,
        handleSettingsChange,

        // 유틸 함수
        formatDate,
        getDateValue,
        isValidDateString,

        // 변경사항 관련
        hasSettingsChanges,
        updateInitialSettings,
        resetSettings,
        getSettingsPayload,
    };
}